import type { IContentBase } from "@/types/content";
import type { ScheduledContentType } from "@/types/contentCalendar";
import type { MaybeRefOrGetter } from "vue";

/**
 * Get formatted and selected content for a specific content ID and task type.
 * @param contentId - The ID of the content to retrieve.
 * @param taskType - The type of task to filter the content by.
 * @param _content - Optional content object to use instead of fetching.
 * @returns An object containing the formatted and selected content.
 */

export function useFormattedAndSelectedContent(
  contentId: MaybeRefOrGetter<string>,
  taskType?: MaybeRefOrGetter<ScheduledContentType | undefined>,
  _content?: MaybeRefOrGetter<IContentBase | null>
) {
  const { data: content, isLoading, isError } = useSingleContentRequest({ contentId });

  const taskId = computed(() => content.value?.parentTaskId || content.value?.taskId);

  const formattedContent = computed(() => {
    const task =
      unref(taskType) ||
      extractScheduledTaskTypeFromContentType(toValue(_content)?.taskType || "") ||
      extractScheduledTaskTypeFromContentType(content.value?.taskType || "");

    const contentData = toValue(_content) || content.value;
    if (!contentData) return null;
    const isWordpress = task === "social-media/wordpress/post-to-wordpress";

    if (isWordpress) {
      return formatTaskResultForWordpress(contentData);
    }
    return formatSocialPostForSelection(contentData)?.[0] || null;
  });

  // just kept for compatibility
  const selectedContent = computed(() => formattedContent.value);

  return {
    content,
    taskId,
    isLoading,
    formattedContent,
    selectedContent,
    isError,
  };
}
